import { useEffect, useRef } from "react";
import { useQueryClient, useIsMutating } from "react-query";
import { useSocketContext } from "@/contexts/socketioContext";
import { CHANGE_FIELD_COMP_MUTATION_KEY } from "@/utils/react-query-hooks/tci";

/**
 * Which columns of `contentType` should render as "Updating data…".
 * Everything is DERIVED — no imperative registration anywhere:
 *
 *   1. While the changeFieldComp MUTATION is in flight → react-query's own
 *      mutation state (found via CHANGE_FIELD_COMP_MUTATION_KEY in the
 *      mutation cache). Keys are the OLD valuePaths — where the columns still
 *      live until the API commits. An errored mutation simply stops being
 *      "loading", so the columns revert by themselves.
 *   2. After success → hold each enqueued migration's NEW valuePath until the
 *      socket tracker reports it terminal (completed OR failed — anything
 *      that's no longer in a progress/waiting state). "Never heard of it"
 *      counts as NOT terminal: a small migration can finish before the sub's
 *      first emit, so silence must hold, not release.
 *   3. Any migration the server reports as running that this session didn't
 *      start (another tab / another user).
 *
 * When a held migration turns terminal: refetch the contentType's listing
 * (its cache still holds pre-migration rows), and only once that lands remove
 * the mutation from the cache — the hold then collapses derivationally, so
 * the real column never renders over stale rows.
 */
export const useFieldMigrationColumnState = (contentType) => {
  const queryClient = useQueryClient();
  const { fieldMigrationStatusByValuePath = {} } = useSocketContext() || {};

  // Subscription only — re-renders this hook whenever any changeFieldComp
  // mutation changes state.
  useIsMutating(CHANGE_FIELD_COMP_MUTATION_KEY);

  const migratingValuePaths = new Set();
  const settledMutations = [];

  if (contentType) {
    // (3) server-reported, regardless of who started it
    Object.entries(fieldMigrationStatusByValuePath).forEach(([key, status]) => {
      if (status === "fieldMigrating" && key.startsWith(`${contentType}:`)) {
        migratingValuePaths.add(key.slice(contentType.length + 1));
      }
    });

    queryClient
      .getMutationCache()
      .getAll()
      .filter((m) => m.options?.mutationKey === CHANGE_FIELD_COMP_MUTATION_KEY)
      .filter((m) => m.state?.variables?.contentType === contentType)
      .forEach((m) => {
        // (1) API in flight — the columns still live at their OLD valuePaths
        if (m.state.status === "loading") {
          (m.state.variables?.fieldCompChanges || []).forEach((change) =>
            migratingValuePaths.add(change.targetValuePath)
          );
        }

        // (2) API succeeded — hold until every enqueued migration is terminal
        // per the tracker, then until the refetch lands. BOTH the old and the
        // new valuePath are held: the tpl-cache update (column re-keys
        // old→new) and this mutation's status flip are separate notifications,
        // so covering both ends makes the render order between them
        // irrelevant. The stale one is a no-op (patch-only merge).
        if (m.state.status === "success") {
          const enqueued = (m.state.data?.data?.results || []).filter(
            (r) => r?.migration?.enqueued
          );
          enqueued.forEach((r) => {
            migratingValuePaths.add(r.newTargetValuePath);
            migratingValuePaths.add(r.oldTargetValuePath);
          });

          const allTerminal = enqueued.every((r) => {
            const status =
              fieldMigrationStatusByValuePath[
                `${contentType}:${r.newTargetValuePath}`
              ];
            return status && status !== "fieldMigrating";
          });
          if (allTerminal) settledMutations.push(m);
        }
      });
  }

  // Terminal → refetch listing once → drop the mutation (releases the hold).
  // Ref-guarded so overlapping renders can't double-fire the refetch.
  const refetchingRef = useRef(new Set());
  useEffect(() => {
    settledMutations.forEach((m) => {
      if (refetchingRef.current.has(m)) return;
      refetchingRef.current.add(m);

      const enqueuedCount = (m.state.data?.data?.results || []).filter(
        (r) => r?.migration?.enqueued
      ).length;

      // Nothing migrated (all changes gated) → nothing to refetch.
      const done = enqueuedCount
        ? queryClient.invalidateQueries({
            queryKey: ["contentListing", "lazy", [contentType]],
          })
        : Promise.resolve();

      done.finally(() => {
        refetchingRef.current.delete(m);
        queryClient.getMutationCache().remove(m);
      });
    });
  });

  return migratingValuePaths;
};
